import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { QueryBody } from "@api/internal/utils/types";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { FeedTagResponseData } from "@routes/Keyword/utils/types";
import {
  generateKeywordUrlPath,
  getMyAccountMyBTCardsProps,
} from "@routes/MyAccount/utils/helpers";
import {
  myBTTagsWithArticle,
  useGetMyBTArticleListProps,
} from "@routes/MyAccount/utils/types";
import { tryCatch } from "@typings/TryCatch";
import axios, { HttpStatusCode } from "axios";

export const useGetMyBTArticleList = ({
  tags,
  tagSize,
  size = 5,
  isPopover = false,
}: useGetMyBTArticleListProps) => {
  const { visitorId } = useUserInfo();

  const [myBTArticleList, setMyBTArticleList] = useState<myBTTagsWithArticle[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);

  const fetchData = useCallback(
    async (page: number) => {
      const tagPageSize = page * 10;
      const tagPageSizeNext = (page + 1) * 10;

      const tagsToFetch = tagSize
        ? tags.slice(0, tagSize)
        : tags.slice(tagPageSize, tagPageSizeNext);

      const fetchTags = tagsToFetch.map((tag) =>
        fetchTagsListingStories({
          urlPath: generateKeywordUrlPath(tag.urlPath),
          size,
        })
      );

      try {
        const responses = await Promise.all(fetchTags);

        const newMyBtArticleList = responses.map((response, index) => {
          if (!response.success) {
            return {
              tag: tagsToFetch[index],
              articles: [],
            };
          }

          const articles = getMyAccountMyBTCardsProps(
            response.data.items,
            isPopover
          );

          return {
            tag: response.data.entity.tag,
            articles,
          };
        });

        const newList = [...myBTArticleList, ...newMyBtArticleList];

        const unique = newList.filter(
          (item, index, self) =>
            index === self.findIndex(({ tag }) => tag.id === item.tag.id)
        );

        setMyBTArticleList(unique);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error my bt article list", error);

        return [];
      } finally {
        setIsLoading(false);
        setPage(page + 1);
      }
    },
    [tags, size, myBTArticleList, tagSize, isPopover]
  );

  useEffect(() => {
    if (!visitorId) return;

    if (tags.length === 0) {
      setIsLoading(false);

      return;
    }

    if (myBTArticleList.length > 0) return;

    fetchData(0);
  }, [myBTArticleList, tags, fetchData, tagSize, visitorId]);

  const { ref } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (tags.length <= myBTArticleList.length) return;
      if (!inView) return;

      fetchData(page);
    },
  });

  return { ref, myBTArticleList, isLoading };
};

const fetchTagsListingStories = async ({
  urlPath,
  size,
}: QueryBody): Promise<FeedTagResponseData> => {
  const payload: QueryBody = {
    urlPath,
    size,
  };

  const request = axios.get<FeedTagResponseData>(
    "/_plat/api/v1/articles/tags",
    { params: payload }
  );

  const { data, error } = await tryCatch(request);

  if (error) {
    return {
      success: false,
      error: { code: HttpStatusCode.BadRequest, message: error.message },
    };
  }

  return data.data;
};
